import { Text } from "@mantine/core";
import {
  business_days,
  CashflowItem,
  every_x_days,
  every_x_months,
  every_x_months_last_day,
  every_x_months_last_working_day,
  every_x_weeks,
  every_x_years,
  IncomesItem,
  specific_dates,
  weekdays,
} from "../../../../backend/types-v2";
import { ChronoUnit } from "@js-joda/core";
import { formatCurrencyFromPence } from "../../../../utils";
import { getStatutoryWeeklyCapPence } from "../../../../backend/build-ledger";
import { scheduleLabels } from "./recurrence";
import { monthName, ordinal, prettyDate, shortDate, shortMonthName } from "./utils";

const chronoUnitAbbrev = (unit: ChronoUnit): string => {
  if (unit === ChronoUnit.DAYS) return "d";
  if (unit === ChronoUnit.WEEKS) return "w";
  if (unit === ChronoUnit.MONTHS) return "mo";
  if (unit === ChronoUnit.YEARS) return "yr";
  return "?";
};

export const descriptionRow = {
  id: "description",
  text: "Description",
  getDisplayValue: (x: CashflowItem) => x.description,
  sortable: true,
} as const;

export const frequencyRow = {
  id: "frequency",
  text: "Frequency",
  getDisplayValue: (x: CashflowItem) => scheduleLabels[x.recurrence.kind],
  sortable: true,
} as const;

export const everyRow = {
  id: "every",
  text: "Every",
  getDisplayValue: (x: CashflowItem) => {
    switch (x.recurrence.kind) {
      case "weekdays":
      case "specific-dates": {
        return "";
      }
      default: {
        return x.recurrence.interval;
      }
    }
  },
} as const;

const scheduleText = (item: CashflowItem) => {
  switch (item.recurrence.kind) {
    case every_x_months_last_working_day:
      return "Monthly · last working day";
    case every_x_months_last_day:
      return "Monthly · last day";
    case every_x_months:
      return `Monthly · ${ordinal(item.recurrence.dayOfMonth)}`;
    case every_x_weeks:
      return `Weekly · ${item.recurrence.weekday.name().slice(0, 3).toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}`;
    case every_x_days:
      return `Every ${item.recurrence.interval} days`;
    case weekdays:
      return "Weekdays";
    case business_days:
      return "Business days";
    case every_x_years:
      return `Yearly · ${ordinal(item.recurrence.dayOfMonth)} ${shortMonthName(item.recurrence.month)}`;
    case specific_dates:
      return item.recurrence.dates.map(shortDate).join(", ");
  }
};

export const scheduleRow = {
  id: "schedule",
  text: "Schedule",
  getDisplayValue: (x: CashflowItem) => scheduleText(x),
  sortable: true,
} as const;

export const onRow = {
  id: "on",
  text: "On",
  getDisplayValue: (x: CashflowItem) => {
    switch (x.recurrence.kind) {
      case every_x_days:
      case every_x_weeks: {
        return x.recurrence.kind === every_x_weeks
          ? x.recurrence.weekday.name()
          : "";
      }
      case every_x_months: {
        return ordinal(x.recurrence.dayOfMonth);
      }
      case every_x_months_last_working_day: {
        return "Last working day";
      }
      case specific_dates: {
        return x.recurrence.dates.map(prettyDate).join("\n");
      }
      case every_x_months_last_day: {
        return "Last day";
      }
      case every_x_years: {
        return `${ordinal(x.recurrence.dayOfMonth)} ${monthName(x.recurrence.month)}`;
      }
      case weekdays:
      case business_days: {
        return "todo";
      }
    }
  },
} as const;

export const amountRow = {
  id: "amount",
  text: "Amount",
  getDisplayValue: (x: CashflowItem) => (
    <Text ff="monospace" style={{ fontVariantNumeric: "tabular-nums" }}>
      {formatCurrencyFromPence(x.amountPence)}
    </Text>
  ),
  sortable: true,
} as const;

export const makeTaxProfileRow = (
  taxProfileIds: Array<{ userId: string; userName: string }>,
) =>
  ({
    id: "taxProfile",
    text: "Tax Profile",
    getDisplayValue: (x: IncomesItem) =>
      x.taxable
        ? taxProfileIds.find((y) => y.userId === x.userId)?.userName ||
          "Unknown"
        : "Not taxed",
    sortable: true,
  }) as const;

export const redundancyRow = {
  id: "redundancy",
  text: "Redundancy",
  getDisplayValue: (x: IncomesItem) => {
    if (!x.redundancy) return "—";
    const capPence = 30_000 * 100;
    const fullWeekly = x.amountPence / 52;
    const weekly =
      x.redundancy.payBasis === "statutory"
        ? Math.min(fullWeekly, getStatutoryWeeklyCapPence(x.redundancy.endDate))
        : fullWeekly;
    const gross = Math.round(weekly * x.redundancy.weeks);
    const excess = Math.max(0, gross - capPence);
    const basis = x.redundancy.payBasis === "statutory" ? "stat" : "full";
    const dayMonth = (d: typeof x.redundancy.endDate) =>
      `${ordinal(d.dayOfMonth())} ${shortMonthName(d.monthValue())}`;
    const paidOn = x.redundancy.paidOn ?? x.redundancy.endDate;
    const endLabel = paidOn.equals(x.redundancy.endDate)
      ? `Ends ${dayMonth(x.redundancy.endDate)}`
      : `Ends ${dayMonth(x.redundancy.endDate)}, paid ${dayMonth(paidOn)}`;
    const parts = [
      endLabel,
      `${x.redundancy.weeks} wk (${basis}) ≈ ${formatCurrencyFromPence(gross)}${
        excess > 0 ? ` (${formatCurrencyFromPence(excess)} taxable)` : " tax free"
      }`,
    ];
    if (x.redundancy.noticeMonths && x.redundancy.noticeMonths > 0) {
      parts.push(`${x.redundancy.noticeMonths}mo PILON`);
    }
    if (x.redundancy.holidayDays && x.redundancy.holidayDays > 0) {
      parts.push(`${x.redundancy.holidayDays}d holiday`);
    }
    return (
      <div>
        {parts.map((p, i) => (
          <div key={i}>· {p}</div>
        ))}
      </div>
    );
  },
} as const;

export const bonusesRow = {
  id: "bonuses",
  text: "Bonuses",
  getDisplayValue: (x: IncomesItem) => {
    if (!x.bonuses || x.bonuses.length === 0) return "—";
    return (
      <div>
        {x.bonuses.map((b, i) => (
          <div key={i}>
            · {shortDate(b.date)}:{" "}
            {b.kind === "fixed"
              ? formatCurrencyFromPence(b.amountPence ?? 0)
              : `${Math.round((b.percentage ?? 0) * 100)}%`}
          </div>
        ))}
      </div>
    );
  },
} as const;

export const parentalLeaveRow = {
  id: "parentalLeave",
  text: "Parental Leave",
  getDisplayValue: (x: IncomesItem) => {
    if (!x.parentalLeavePolicy) return "—";
    const rules = x.parentalLeavePolicy.sequentialRules
      .map(
        (r) =>
          `${r.amountOfChronoUnits}${chronoUnitAbbrev(r.chronoUnits)} @ ${r.amountType === "smp" ? "SMP" : `${Math.round(r.percentage * 100)}%`}`,
      )
      .join(", ");
    return `From ${shortDate(x.parentalLeavePolicy.startsFrom)}: ${rules}`;
  },
} as const;
